import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import type { JSX } from 'react';

import { ensureTaskTrackerEnabled } from '@/features/tasks/guard.js';
import { parseTasksSearch } from '@/features/tasks/tasks-search.js';
import { TasksPage } from '@/features/tasks/TasksPage.js';

/**
 * `/tasks`: the tasks people gave the signed-in user and the tasks the user gave others, behind the
 * fail-closed task tracker flag. The address holds the Board view (`?view=board`), the task open in
 * the panel (`?task=<id>`) and the filters (`?source=`, `?direction=`, `?status=`, `?priority=`,
 * `?author=`, `?q=`, `?completed=all`). The flag is read again when the page is
 * entered, not on each change of the address within it: a search typed letter by letter would
 * otherwise read the config on every key, and one failed read would send the person home mid-word.
 */
export const Route = createFileRoute('/tasks')({
  validateSearch: parseTasksSearch,
  beforeLoad: ({ cause }) =>
    cause === 'stay' ? undefined : ensureTaskTrackerEnabled(),
  component: TasksRoute,
});

function TasksRoute(): JSX.Element {
  const search = useSearch({ from: '/tasks' });
  const navigate = useNavigate();
  return (
    <TasksPage
      search={search}
      onSearchChange={(next) =>
        void navigate({ to: '/tasks', search: next, replace: true })
      }
      onOpenConversation={(conversationId) =>
        void navigate({ to: '/chats', search: { conversationId } })
      }
    />
  );
}
